import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  OnboardingData,
  RatingData,
  UserState,
  AnalysisHistoryItem,
} from '../types';

// Generate unique user ID (compatible with all browsers)
const generateUserId = (): string => {
  // Fallback for browsers without crypto.randomUUID
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
  return uuid;
};

interface AppStore {
  // State
  currentStep: number;
  onboarding: OnboardingData;
  rating: RatingData;
  user: UserState;
  analysis: AnalysisHistoryItem | null;
  isAnalyzing: boolean;

  // Actions
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateOnboarding: (data: Partial<OnboardingData>) => void;
  updateRating: (data: Partial<RatingData>) => void;
  updateUser: (data: Partial<UserState>) => void;
  setAnalysis: (result: AnalysisHistoryItem) => void;
  setIsAnalyzing: (status: boolean) => void;
  resetStore: () => void;
  initializeUser: () => void;
}

const initialOnboarding: OnboardingData = {
  ratingType: null,
  gender: null,
  ageRange: null,
  focusAreas: [],
  improvementGoals: [],
  selfPerceivedAttractiveness: 5,
  appearanceAnxiety: null,
  motivationLevel: null,
  complimentFrequency: null,
};

const initialRating: RatingData = {
  frontPhoto: null,
  frontPhotoPreview: null,
  sidePhoto: null,
  sidePhotoPreview: null,
  analysisId: null,
};

const initialUser: UserState = {
  userId: '',
  isSubscribed: false,
  subscriptionType: null,
  subscriptionExpiresAt: null,
  isAuthenticated: false,
};

export const useStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Initial state
      currentStep: 1,
      onboarding: initialOnboarding,
      rating: initialRating,
      user: initialUser,
      analysis: null,
      isAnalyzing: false,

      // Actions
      setCurrentStep: (step) => set({ currentStep: step }),

      nextStep: () => set((state) => ({ currentStep: state.currentStep + 1 })),

      prevStep: () =>
        set((state) => ({
          currentStep: Math.max(1, state.currentStep - 1),
        })),

      updateOnboarding: (data) =>
        set((state) => ({
          onboarding: { ...state.onboarding, ...data },
        })),

      updateRating: (data) =>
        set((state) => ({
          rating: { ...state.rating, ...data },
        })),

      updateUser: (data) =>
        set((state) => ({
          user: { ...state.user, ...data },
        })),

      setAnalysis: (result) => set({ analysis: result }),

      setIsAnalyzing: (status) => set({ isAnalyzing: status }),

      resetStore: () =>
        set({
          currentStep: 1,
          onboarding: initialOnboarding,
          rating: initialRating,
          analysis: null,
          isAnalyzing: false,
        }),

      initializeUser: () => {
        const { user } = get();
        if (!user.userId) {
          set({
            user: {
              ...user,
              userId: generateUserId(),
            },
          });
          return;
        }

        if (user.userId.startsWith('user_')) {
          set({
            user: {
              ...user,
              userId: user.userId.replace(/^user_/, ''),
            },
          });
        }
      },
    }),
    {
      name: 'umax-store',
      partialize: (state) => ({
        user: state.user,
        onboarding: state.onboarding,
        currentStep: state.currentStep,
      }),
    }
  )
);
